// @flow
import { put } from 'redux-saga/effects';

import axios from 'axios';
import { type $AxiosError, type $AxiosXHR } from 'axios';
import { updateToken, getToken } from './tokenStorage';
import { SubmissionError } from 'redux-form';
import { ERROR_MODAL } from '../modules/ModalConductor/modalTypes';
import { showModal } from '../modules/ModalConductor/reducer';
import { logout } from '../App/reducer';
import { setSignInMessage } from '../App/SignIn/reducer';
import { store } from '..';
// import { showModal } from '../Wallet/modules/ModalWrapper/reducer';
// import { SESSION_EXPIRED } from '../Wallet/modules/ModalWrapper/modalTypes';

type AnyResponse = { [key: string]: any };

export const postRequest = async (url: string, payload: {}, options: {}) =>
  await request(url, payload, 'post', options);

export const getRequest = async (
  url: string,
  params: {} = {},
  options: {} = {}
) => await request(url, params, 'get', options);

export const deleteRequest = async (url: string) =>
  await request(url, {}, 'delete');

export const patchRequest = async (url: string, payload: {}) =>
  await request(url, payload, 'patch');

export const putRequest = async (url: string, payload: {}) =>
  await request(url, payload, 'put');

export const request = async (
  url: string,
  payload: {},
  method: 'post' | 'patch' | 'put' | 'get' | 'delete',
  options: {} = {}
) => {
  try {
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };
    let response;
    if (method === 'get' || method === 'delete') {
      response = await paramRequest(url, payload, method, headers, options);
    } else {
      response = await bodyRequest(url, payload, method, headers, options);
    }
    updateToken(response);
    return response;
  } catch (error) {
    if (
      error.response &&
      error.response.status === 401 &&
      !error.response.headers['x-token']
    ) {
      store.dispatch(
        setSignInMessage(
          'Your session has expired due to inactivity, please sign in to continue'
        )
      );
      store.dispatch(logout());
    }
    throw error;
  }
};

const bodyRequest = async (
  url: string,
  data: {},
  method: 'post' | 'patch' | 'put',
  headers: { Authorization: string },
  options: {}
) =>
  // $FlowFixMe
  (await axios({
    method,
    url,
    data,
    headers,
    ...options
  }): $AxiosXHR<AnyResponse>);

const paramRequest = async (
  url: string,
  params: {},
  method: 'get' | 'delete',
  headers: { Authorization: string },
  options: {}
) =>
  // $FlowFixMe
  (await axios({
    method,
    url,
    params,
    headers,
    ...options
  }): $AxiosXHR<AnyResponse>);

type ErrorResponse = {
  data: {
    source?: { [key: string]: string },
    details: string,
    error?: string
  }
};
export const handleThrowSubmissionError = (response: ?ErrorResponse) => {
  let errors = { _error: 'Unable to submit form' };
  if (response) {
    if (response.data.source) {
      errors = { ...response.data.source, ...errors };
    }
    const responseMessage = response.data.error || response.data.details;
    if (responseMessage) {
      errors._error = responseMessage;
    }
  }
  throw new SubmissionError(errors);
};

export const rethrowSubmissionError = (error: Error | SubmissionError) => {
  if (error instanceof SubmissionError) {
    throw error;
  }
};

export const defaultSubmitHandler = (
  url: string,
  method: 'post' | 'patch' | 'put' = 'post'
) => async (values: {}) => {
  try {
    const response = await request(url, values, method);
    return response.data;
  } catch (error) {
    handleThrowSubmissionError(error.response);
  }
};

export const deleteSubmitHandler = (url: string) => async () => {
  try {
    const response = await deleteRequest(url);
    return response.data;
  } catch (error) {
    handleThrowSubmissionError(error.response);
  }
};

export function* handleSagaError(
  error: $AxiosError<{ message?: string, details?: string }>,
  defaultMessage: string = 'Unfortunately, an error has occurred. A member of our team is trying to solve the issue and will contact you shortly. Please try again later.',
  callback?: (message?: string) => {}
): Generator<*, *, *> {
  let message = defaultMessage;
  if (
    error.response &&
    (error.response.data.message || error.response.data.details)
  ) {
    message = error.response.data.message || error.response.data.details;
  }
  if (callback) {
    yield put(callback(message));
  } else {
    yield put(showModal(ERROR_MODAL, { message }));
  }
}
